import { useUserOrgId } from '@/auth/authentication';
import { ClinicianLabel } from '@/components/formFields/QhpSelect';
import { QualificationsFilterField } from '@/components/formFields/QualificationsField';
import {
    AxleAutocompleteMultiselectWithFullWidthChips,
    AxleAutocompleteMultiselectWithShortChips,
} from '@/components/formFields/multiselectFields';
import {
    AxleDialogActions,
    AxleDialogContent,
    AxleDialogPrimaryButton,
    AxleDialogSoftDeleteButton,
} from '@/components/layout/AxleDialog';
import { VerticalStack } from '@/components/utils/linearLayouts';
import { UNASSIGNED_QHP_ID } from '@/constants/visit';
import { ManagedAxleDialog, ManagedAxleDialogProps } from '@/dialogs/ManagedAxleDialog';
import { useAppConfig } from '@/fetching/api/appConfig';
import { AxleSuspense, DIALOG_SKELETON_HEIGHT } from '@/fetching/suspense';
import { FeatureFlag } from '@/types/featureFlag';
import { OrderStateFilter, orderStatesToOrderStateFilter } from '@/types/order';
import {
    VisitOutcome,
    VisitPriority,
    VisitState,
    visitOutcomeFromZodCode,
    visitOutcomeToZod,
    visitPriorityToZod,
} from '@/types/visit';
import { atomWithSessionStorage } from '@/utils/browserStorage';
import { orgPrefixedName } from '@/utils/formatting';
import { useInputState } from '@/utils/inputState';
import { useFeatureFlagEnabled } from '@/utils/useFeatureFlagEnabled';
import { gql, useSuspenseQuery } from '@apollo/client';
import {
    Group,
    LocalHospital,
    MedicalInformation,
    People,
    Place,
    SignalCellularAlt,
} from '@mui/icons-material';
import { Grid } from '@mui/material';
import { VisitFilter, VisitRequestFilter } from 'gql-types';
import { useAtom, useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { z } from 'zod';

export const visitFiltersAtom = atomWithSessionStorage(
    'visitFilters',
    {
        // The keys of this object are exactly the keys sent to the backend and
        //  parsed by it, so don’t change them.
        qhps: [],
        orgs: [],
        territories: [],
        services: [],
        outcomes: [],
        qhp_teams_uuids: [],
        qualifications_uuids: [],
        patients: [],
        service_areas: [],
        priority_codes: [],
        discipline_uuids: [],
        orderState: [],
    },
    z.object({
        qhps: z.array(z.string()),
        orgs: z.array(z.string()),
        territories: z.array(z.string()),
        services: z.array(z.string()),
        outcomes: z.array(z.string()),
        qhp_teams_uuids: z.array(z.string()),
        qualifications_uuids: z.array(z.string()),
        patients: z.array(z.string()),
        service_areas: z.array(z.string()),
        priority_codes: z.array(z.string()),
        discipline_uuids: z.array(z.string()),
        orderState: z.optional(z.array(z.nativeEnum(OrderStateFilter)).nullable()),
    }),
);

const VISIT_FILTER_QUERY_OPTIONS = gql`
    query VisitFilterOptions {
        viewableVisitStates
        viewableVisitOutcomes
        organizations {
            items {
                id
                name
            }
        }
        clinicians {
            items {
                id
                firstName
                lastName
                logoUrl
            }
        }
        territories {
            items {
                id
                name
            }
        }
        clinicianTeams {
            items {
                id
                name
            }
        }
        serviceAreas {
            items {
                id
                name
            }
        }
        services {
            items {
                organization {
                    id
                    name
                }
                id
                name
            }
        }
        disciplines {
            items {
                id
                displayName
            }
        }
    }
`;
interface IFilterClinician {
    id: string;
    firstName: string;
    lastName: string;
    logoUrl: string | null;
}
interface IFilterVisitOptions {
    organizations: {
        items: {
            id: string;
            name: string;
        }[];
    } | null;
    clinicianTeams: {
        items: {
            id: string;
            name: string;
        }[];
    } | null;
    territories: {
        items: {
            id: string;
            name: string;
        }[];
    } | null;
    serviceAreas: {
        items: {
            id: string;
            name: string;
        }[];
    } | null;
    clinicians: {
        items: IFilterClinician[];
    } | null;
    services: {
        items: {
            id: string;
            name: string;
            organization: {
                id: string;
                name: string;
            };
        }[];
    } | null;
    disciplines: {
        items: {
            id: string;
            displayName: string;
        }[];
    } | null;
    viewableVisitOutcomes: VisitOutcome[];
    viewableVisitStates: VisitState[];
}

const VisitFilterDialogContent = ({ close }: { close: () => void }) => {
    const appConfig = useAppConfig();
    const hasOrdersFeature = useFeatureFlagEnabled(FeatureFlag.ORDERS);

    const { data } = useSuspenseQuery<IFilterVisitOptions>(VISIT_FILTER_QUERY_OPTIONS);

    // Load state from atom into the dialog.
    const [filterState, setFilterState] = useAtom(visitFiltersAtom);

    const qhpsState = useInputState(filterState.qhps);
    const orgsState = useInputState(filterState.orgs);
    const territoriesState = useInputState(filterState.territories);
    const servicesState = useInputState(filterState.services);
    const outcomesState = useInputState(filterState.outcomes);
    const qhpTeamsState = useInputState<string[]>(filterState.qhp_teams_uuids);
    const qualificationsState = useInputState<string[]>(filterState.qualifications_uuids);
    const patientsState = useInputState(filterState.patients);
    const serviceAreaState = useInputState(filterState.service_areas);
    const priorityState = useInputState(filterState.priority_codes);
    const disciplineState = useInputState(filterState.discipline_uuids);
    const orderStateFilterState = useInputState(filterState.orderState ?? []);
    // Save state from dialog into the atom.
    const saveFilterState = () => {
        setFilterState({
            ...filterState,
            ...{
                qhps: qhpsState.value,
                orgs: orgsState.value,
                territories: territoriesState.value,
                services: servicesState.value,
                outcomes: outcomesState.value,
                qhp_teams_uuids: qhpTeamsState.value,
                qualifications_uuids: qualificationsState.value,
                patients: patientsState.value,
                service_areas: serviceAreaState.value,
                priority_codes: priorityState.value,
                discipline_uuids: disciplineState.value,
                orderState: orderStateFilterState.value,
            },
        });
    };

    // This isn’t the same as calling the `reset` method on the
    //  input states, because if a user chose say 2 QHPs to filter
    //  on and reloaded the page, the initial value for qhpState
    //  would be those two QHPs, and we want to clear the filter
    //  entirely, not reset to what it was on the page load.
    const clearAll = () => {
        qhpsState.setInputState([]);
        orgsState.setInputState([]);
        territoriesState.setInputState([]);
        servicesState.setInputState([]);
        outcomesState.setInputState([]);
        qhpTeamsState.setInputState([]);
        qualificationsState.setInputState([]);
        patientsState.setInputState([]);
        serviceAreaState.setInputState([]);
        priorityState.setInputState([]);
        disciplineState.setInputState([]);
        orderStateFilterState.setInputState([]);
    };

    const userOrgId = useUserOrgId();
    const showOrgNameForServices =
        data.services?.items.some((service) => service.organization.id !== userOrgId) ?? false;

    const cliniciansWithUnassigned: IFilterClinician[] = [
        {
            id: UNASSIGNED_QHP_ID,
            firstName: 'Unassigned',
            lastName: '',
            logoUrl: null,
        } as IFilterClinician,
    ].concat(data.clinicians?.items ?? []);

    return (
        <>
            <AxleDialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={6} md={6}>
                        <VerticalStack>
                            {!!data.clinicians?.items.length && (
                                <AxleAutocompleteMultiselectWithFullWidthChips
                                    label={'Assigned Clinicians'}
                                    inputState={qhpsState}
                                    options={cliniciansWithUnassigned}
                                    optionToValue={(option) => option.id}
                                    optionToLabel={(option) =>
                                        option.firstName +
                                        (option.lastName ? ' ' + option.lastName : '')
                                    }
                                    renderOption={(option) => <ClinicianLabel option={option} />}
                                    chipIcon={<MedicalInformation />}
                                />
                            )}
                            {appConfig.qhp.teams_view && (
                                <AxleAutocompleteMultiselectWithFullWidthChips
                                    label={'Clinician Teams'}
                                    inputState={qhpTeamsState}
                                    options={data.clinicianTeams?.items ?? []}
                                    optionToValue={(option) => option.id}
                                    optionToLabel={(option) => option.name}
                                />
                            )}
                            {appConfig.visit.qualifications && (
                                <QualificationsFilterField
                                    fromSchedulePage={false}
                                    inputState={qualificationsState}
                                />
                            )}
                            {/* Only show the Partner filter if there are multiple organizations. */}
                            {!!data.organizations?.items && data.organizations.items.length > 1 && (
                                <AxleAutocompleteMultiselectWithFullWidthChips
                                    // Calling this "Partner" for consistency with the <VisitTable/> column name.
                                    label={'Partner'}
                                    inputState={orgsState}
                                    options={data.organizations.items}
                                    optionToValue={(option) => option.id}
                                    optionToLabel={(option) => option.name}
                                    chipIcon={<Group />}
                                />
                            )}
                            <AxleAutocompleteMultiselectWithFullWidthChips
                                label={'Priorities'}
                                inputState={priorityState}
                                options={Object.values(VisitPriority).map(visitPriorityToZod)}
                                optionToValue={(option) => option.code}
                                optionToLabel={(option) => option.name}
                                sortOptions={false}
                                chipIcon={<SignalCellularAlt />}
                            />
                            {hasOrdersFeature && (
                                <AxleAutocompleteMultiselectWithShortChips
                                    label={'Order Status'}
                                    inputState={orderStateFilterState}
                                    options={[
                                        { id: OrderStateFilter.ACTIVE, displayName: 'Active' },
                                        {
                                            id: OrderStateFilter.DISCONTINUED,
                                            displayName: 'Discontinued',
                                        },
                                        { id: OrderStateFilter.PAUSED, displayName: 'Paused' },
                                    ]}
                                    optionToValue={(option) => option.id}
                                    optionToLabel={(option) => option.displayName}
                                />
                            )}
                        </VerticalStack>
                    </Grid>

                    <Grid item xs={6} md={6}>
                        <VerticalStack>
                            {!!data.disciplines?.items.length && (
                                <AxleAutocompleteMultiselectWithFullWidthChips
                                    label={'Disciplines'}
                                    inputState={disciplineState}
                                    options={data.disciplines.items}
                                    optionToValue={(option) => option.id}
                                    optionToLabel={(option) => option.displayName}
                                />
                            )}
                            {!!data.services?.items.length && (
                                <AxleAutocompleteMultiselectWithFullWidthChips
                                    label={'Services'}
                                    inputState={servicesState}
                                    options={data.services.items}
                                    optionToValue={(option) => option.id}
                                    optionToLabel={(option) =>
                                        orgPrefixedName(
                                            {
                                                name: option.name,
                                                organization: option.organization,
                                            },
                                            showOrgNameForServices,
                                        )
                                    }
                                    chipIcon={<LocalHospital />}
                                />
                            )}
                            {!!data.viewableVisitOutcomes.length && (
                                <AxleAutocompleteMultiselectWithFullWidthChips
                                    label={'Visit Outcome'}
                                    inputState={outcomesState}
                                    options={data.viewableVisitOutcomes.map(visitOutcomeToZod)}
                                    optionToValue={(option) => option.code}
                                    optionToLabel={(option) => option.name}
                                />
                            )}
                            {!!data.territories?.items.length && (
                                <AxleAutocompleteMultiselectWithFullWidthChips
                                    label={'Territories'}
                                    inputState={territoriesState}
                                    options={data.territories.items}
                                    optionToValue={(option) => option.id}
                                    optionToLabel={(option) => option.name}
                                    chipIcon={<Place />}
                                />
                            )}
                            {!!data.serviceAreas?.items.length && (
                                <AxleAutocompleteMultiselectWithFullWidthChips
                                    label={'Service Areas'}
                                    inputState={serviceAreaState}
                                    options={data.serviceAreas.items}
                                    optionToValue={(option) => option.id}
                                    optionToLabel={(option) => option.name}
                                    chipIcon={<People />}
                                />
                            )}
                        </VerticalStack>
                    </Grid>
                    {/* We may want to add back the status filter in this dialog in the future */}
                    {/* <Grid item xs={6} md={3}>
                        {filtersConfig.status && (
                            <AxleCheckboxesField
                                label={'Status'}
                                inputState={statusState}
                                options={filtersConfig.status}
                                optionToValue={(option) => option.code}
                                optionToLabel={(option) => option.name}
                            />
                        )}
                    </Grid> */}
                </Grid>
            </AxleDialogContent>
            <AxleDialogActions>
                <AxleDialogSoftDeleteButton label={'Clear All'} onClick={clearAll} />
                <AxleDialogPrimaryButton
                    label={'Apply'}
                    onClick={() => {
                        saveFilterState();
                        close();
                    }}
                />
            </AxleDialogActions>
        </>
    );
};

export const useVisitFilterDialogFilters = (): VisitFilter => {
    const filterState = useAtomValue(visitFiltersAtom);
    return useMemo((): VisitFilter => {
        const filters: VisitFilter = {};
        const assignedQhps = filterState.qhps.filter((id) => id !== UNASSIGNED_QHP_ID);
        const includeUnassigned = filterState.qhps.includes(UNASSIGNED_QHP_ID);
        if (includeUnassigned && assignedQhps.length) {
            filters.OR = {
                needsClinicians: { exact: true },
                assignedClinicians: { id: { inList: assignedQhps } },
            };
        } else if (includeUnassigned) {
            filters.needsClinicians = { exact: true };
        } else if (assignedQhps.length) {
            filters.assignedClinicians = { id: { inList: assignedQhps } };
        }
        if (filterState.qhp_teams_uuids.length) {
            filters.assignedClinicianTeams = { id: { inList: filterState.qhp_teams_uuids } };
        }
        if (filterState.orgs.length) {
            filters.organization = { id: { inList: filterState.orgs } };
        }
        if (filterState.territories.length) {
            filters.territory = { id: { inList: filterState.territories } };
        }
        if (filterState.services.length) {
            filters.services = { id: { inList: filterState.services } };
        }
        if (filterState.outcomes.length) {
            filters.outcome = { inList: filterState.outcomes.map(visitOutcomeFromZodCode) };
        }
        if (filterState.qualifications_uuids.length) {
            filters.qualifications = { id: { inList: filterState.qualifications_uuids } };
        }
        if (filterState.patients.length) {
            filters.patient = { id: { inList: filterState.patients } };
        }
        if (filterState.service_areas.length) {
            filters.serviceArea = { id: { inList: filterState.service_areas } };
        }
        if (filterState.priority_codes.length) {
            filters.priority = { inList: filterState.priority_codes as VisitPriority[] };
        }
        if (filterState.discipline_uuids.length) {
            filters.disciplines = { id: { inList: filterState.discipline_uuids } };
        }
        if (filterState.orderState?.length) {
            filters.relatedOrder = orderStatesToOrderStateFilter(filterState.orderState);
        }
        return filters;
    }, [filterState]);
};

export const useVisitRequestFilterDialogFilters = (): VisitRequestFilter => {
    const filterState = useAtomValue(visitFiltersAtom);
    return useMemo((): VisitRequestFilter => {
        const hasNonNoneOutcome = filterState.outcomes.some((o) => o !== VisitOutcome.NONE);
        if (hasNonNoneOutcome) {
            return { id: { inList: [] } };
        }
        const filters: VisitRequestFilter = {};
        const assignedQhps = filterState.qhps.filter((id) => id !== UNASSIGNED_QHP_ID);
        const includeUnassigned = filterState.qhps.includes(UNASSIGNED_QHP_ID);
        if (includeUnassigned && assignedQhps.length) {
            filters.OR = {
                needsClinicians: { exact: true },
                assignedClinicians: { id: { inList: assignedQhps } },
            };
        } else if (includeUnassigned) {
            filters.needsClinicians = { exact: true };
        } else if (assignedQhps.length) {
            filters.assignedClinicians = { id: { inList: assignedQhps } };
        }
        if (filterState.qhp_teams_uuids.length) {
            filters.assignedClinicianTeams = { id: { inList: filterState.qhp_teams_uuids } };
        }
        if (filterState.territories.length) {
            filters.territories = { id: { inList: filterState.territories } };
        }
        if (filterState.services.length) {
            filters.services = { id: { inList: filterState.services } };
        }
        if (filterState.patients.length) {
            filters.patient = { id: { inList: filterState.patients } };
        }
        if (filterState.discipline_uuids.length) {
            filters.disciplines = { id: { inList: filterState.discipline_uuids } };
        }
        if (filterState.priority_codes.length) {
            filters.priority = { inList: filterState.priority_codes as VisitPriority[] };
        }
        if (filterState.orderState?.length) {
            filters.relatedOrder = orderStatesToOrderStateFilter(filterState.orderState);
        }

        return filters;
    }, [filterState]);
};

export const useNumVisitDialogFilters = (): number => {
    const filterState = useAtomValue(visitFiltersAtom);
    return useMemo(
        () => Object.values(filterState).filter((v) => Array.isArray(v) && v.length > 0).length,
        [filterState],
    );
};

export const VisitFilterDialog = ({ onClose }: ManagedAxleDialogProps) => {
    return (
        <ManagedAxleDialog
            dataTestId='visits-filter-dialog'
            title={'Visit Filters'}
            width={'sm'}
            onClose={onClose}
        >
            {({ close }) => (
                <AxleSuspense skeletonHeight={DIALOG_SKELETON_HEIGHT}>
                    <VisitFilterDialogContent close={close} />
                </AxleSuspense>
            )}
        </ManagedAxleDialog>
    );
};
